import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import appSettings from "appsettings.json";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function TermsAndConditions() {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [termsPdfDataUrl, setTermsPdfDataUrl] = useState("");

  const token = localStorage.getItem("token");
  const isTocAccepted = localStorage.getItem("isTocAccepted") === "true";

  const apiBaseUrl = useMemo(() => {
    return appSettings.baseUrl;
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    if (isTocAccepted) {
      navigate("/dashboard");
    }
  }, [token, isTocAccepted, navigate]);

  useEffect(() => {
    let isCancelled = false;

    const loadHelpFile = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}${appSettings.endpoints.helpFileBase64}`);
        if (!response.ok) {
          throw new Error("Failed to load help file");
        }

        const data = await response.json();
        const base64 = data?.data;
        const contentType = data?.contentType || "application/pdf";

        if (!base64) {
          throw new Error("Invalid help file payload");
        }

        if (!isCancelled) {
          setTermsPdfDataUrl(`data:${contentType};base64,${base64}`);
        }
      } catch (loadError) {
        console.error("Help file load error:", loadError);
        if (!isCancelled) {
          setError("Unable to load terms and conditions. Please try again.");
        }
      }
    };

    loadHelpFile();

    return () => {
      isCancelled = true;
    };
  }, [apiBaseUrl]);

  const handleSubmit = async () => {
    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    if (!isChecked) return;

    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiBaseUrl}${appSettings.endpoints.acceptToc}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to accept terms and conditions");
      }

      localStorage.setItem("isTocAccepted", "true");
      navigate("/dashboard");
    } catch (submitError) {
      console.error("Accept TOC error:", submitError);
      setError("Unable to accept terms and conditions. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isTocAccepted");
    navigate("/authentication/sign-in");
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Terms and Conditions
          </MDTypography>
        </MDBox>

        <MDBox pt={3} pb={3} px={3}>
          <MDBox
            sx={{
              border: "1px solid #d7d7d7",
              borderRadius: "8px",
              overflow: "hidden",
              height: "360px",
              mb: 2,
              position: "relative",
              zIndex: 0,
            }}
          >
            <iframe
              title="Terms and Conditions"
              src={termsPdfDataUrl}
              width="100%"
              height="100%"
              style={{ border: "none", display: "block" }}
            />
          </MDBox>

          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Checkbox
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              sx={{
                color: "#2e7d32",
                "&.Mui-checked": {
                  color: "#2e7d32",
                },
              }}
            />
            <MDTypography
              variant="button"
              sx={{ cursor: "pointer" }}
              onClick={() => setIsChecked((prev) => !prev)}
            >
              I have read and accept the Terms and Conditions.
            </MDTypography>
          </MDBox>

          {error && (
            <MDBox mt={1} textAlign="center">
              <MDTypography variant="button" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            </MDBox>
          )}

          <MDBox mt={2}>
            <MDButton
              variant="gradient"
              color="success"
              fullWidth
              onClick={handleSubmit}
              disabled={!isChecked || isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Accept & Continue"}
            </MDButton>
          </MDBox>
          <MDBox mt={1}>
            <MDButton variant="outlined" color="error" fullWidth onClick={handleReject}>
              Reject & Logout
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default TermsAndConditions;
